import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Link,
    CircularProgress,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import axios from 'axios';

const ForgotPasswordPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL;

    // Initialize CSRF token
    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL;
        const fetchData = async () => {
            await fetch(`${API_URL}/welcome`, {
                method: 'GET',
                credentials: 'include',
            });
        };
        fetchData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');

        try {
            const response = await axios.post(
                `${API_URL}/forgot-password/request`,
                { email: email },
                {
                    withCredentials: true,
                    withXSRFToken: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            setMessage(response.data.message);
            setIsSubmitted(true);
            setEmail('');
        } catch (error) {
            setMessage('An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Grid container sx={{ minHeight: '100vh', width: '100%' }}>
            {/* Left panel - Gradient background with logo */}
            <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                    background: `linear-gradient(rgba(11, 21, 55, 0.75), rgba(11, 21, 55, 0.75)), url('/Moonlight-Connect-login-background.svg') #0B1537`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: isMobile ? '200px' : '100vh',
                    p: 4,
                }}
            >
                <Box
                    component="img"
                    sx={{
                        width: isMobile ? '280px' : isTablet ? '500px' : '750px',
                    }}
                    src="/MOONLIGHT-quality-logo.svg"
                    alt="Moonlight Quality"
                />
            </Grid>

            {/* Right panel - Forgot password form */}
            <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: { xs: 3, sm: 4, md: 6 },
                    background: '#F3F3F3',
                }}
            >
                <Box
                    sx={{
                        textAlign: 'left',
                        width: '100%',
                        maxWidth: '400px',
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            mb: 1,
                            fontFamily: 'Montserrat, sans-serif',
                            fontWeight: 700,
                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        }}
                    >
                        FORGOT PASSWORD
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 3,
                            color: '#545454',
                        }}
                    >
                        {
                            "Enter your email address and we'll send you a link to reset your password."
                        }
                    </Typography>
                </Box>

                {!isSubmitted ? (
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            maxWidth: '400px',
                        }}
                    >
                        <TextField
                            placeholder="Email Address"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            margin="normal"
                            autoFocus
                            sx={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '10px',
                                boxShadow: 5,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    '& fieldset': { borderColor: 'transparent' },
                                    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.15)' },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(0,0,0,0.25)',
                                        borderWidth: '1px',
                                    },
                                },
                                input: { fontSize: '16px' },
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isLoading || !email.trim()}
                            fullWidth
                            sx={{
                                background: '#FBBB1D',
                                height: '56px',
                                mt: 3,
                                mb: 2,
                                fontFamily: 'Montserrat, sans-serif',
                                fontWeight: 700,
                                fontSize: { xs: 16, sm: 18, md: 20 },
                                borderRadius: '10px',
                                boxShadow: 5,
                                color: '#fff',
                                '&:hover': {
                                    background: '#E5AA15',
                                },
                                '&.Mui-disabled': {
                                    background: '#FBBB1D',
                                    opacity: 0.7,
                                    color: '#fff',
                                },
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress size={28} sx={{ color: '#fff' }} />
                            ) : (
                                'SEND RESET LINK'
                            )}
                        </Button>

                        {message && (
                            <Typography
                                sx={{
                                    mt: 1,
                                    color: '#545454',
                                    textAlign: 'center',
                                }}
                            >
                                {message}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '400px',
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 3,
                                color: '#545454',
                                lineHeight: 1.6,
                            }}
                        >
                            {message}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setIsSubmitted(false);
                                setMessage('');
                            }}
                            fullWidth
                            sx={{
                                background: '#FBBB1D',
                                height: '56px',
                                mb: 2,
                                fontFamily: 'Montserrat, sans-serif',
                                fontWeight: 700,
                                fontSize: { xs: 16, sm: 18 },
                                borderRadius: '10px',
                                boxShadow: 5,
                                color: '#fff',
                                '&:hover': {
                                    background: '#E5AA15',
                                },
                            }}
                        >
                            SEND ANOTHER LINK
                        </Button>
                    </Box>
                )}

                <Box
                    sx={{
                        textAlign: 'left',
                        width: '100%',
                        maxWidth: '400px',
                        mt: 1,
                    }}
                >
                    <Link
                        href="/"
                        underline="hover"
                        sx={{
                            color: '#545454',
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                        }}
                    >
                        Back to Sign In
                    </Link>
                </Box>
            </Grid>
        </Grid>
    );
};

export default ForgotPasswordPage;
